/**
 * DualgsViewer-XR.jsx — WebXR helpers for the splat sequence player.
 *
 * Split out of DualgsViewer.jsx so the main file stays focused on rendering
 * and playback. The host wires four touch-points:
 *
 *   1. `const xrSupport = useXRSupport(enableXR)`                — probe
 *   2. `const { xrActive, enterXR, exitXR, grabRef } = useXRSession({...})`
 *   3. inside the scene effect, after renderer/scene/modelAnchor exist:
 *        `const { updateXrGrab } = setupXRControllers({ ..., grabRef })`
 *      then call `updateXrGrab()` once per animate tick (before render).
 *   4. `<XRButton ... />` in the toolbar.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import {
  isIOSPhone,
  loadVariantLaunchSDK,
  redirectToVariantLaunchAR,
  hasAutoStartMarker,
  clearAutoStartMarker,
} from './DualgsViewer-XR-ios.jsx'

// Re-export so the host can keep importing it from the main XR module.
export { loadVariantLaunchSDK }

// Room-scale default applied to `modelAnchor` on XR entry. Tuned for the
// ~10-unit-diameter splat captures used in this repo; very different scenes
// may need to override these (and the user can always re-grab to fix).
export const XR_PLACEMENT_DEFAULTS = {
  position: [0, 0.5, -1.5], // 1.5 m in front, half a meter above the floor
  scale: 0.3,
}

// Probe immersive-vr / immersive-ar support so the toolbar can show or hide
// the XR button. iPhone short-circuits to `ar: true` (no native WebXR there;
// `enterXR` redirects to LaunchAR for the actual experience). Other browsers
// go through `navigator.xr.isSessionSupported` per mode.
export function useXRSupport(enableXR) {
  const [xrSupport, setXrSupport] = useState({ vr: false, ar: false })
  useEffect(() => {
    if (!enableXR) return
    const xr = typeof navigator !== 'undefined' ? navigator.xr : null
    if (xr) {
      // Real WebXR is present — desktop/standalone headsets, Android Chrome, or
      // the Variant Launch AR browser on iOS (which injects navigator.xr).
      // Probe per mode so the actual session path is what we advertise.
      let cancelled = false
      Promise.all([
        xr.isSessionSupported('immersive-vr').catch(() => false),
        xr.isSessionSupported('immersive-ar').catch(() => false),
      ]).then(([vr, ar]) => {
        if (!cancelled) setXrSupport({ vr: !!vr, ar: !!ar })
      })
      return () => { cancelled = true }
    }
    // No native WebXR. iPhone/iPod Safari can still reach AR via the Variant
    // Launch redirect, so advertise AR there; nothing for other browsers.
    if (isIOSPhone()) setXrSupport({ vr: false, ar: true })
  }, [enableXR])
  return xrSupport
}

// Owns session state + enter/exit callbacks. The host passes refs to live
// renderer/canvas/anchor so this hook doesn't depend on three.js init timing.
// `grabRef` is returned so the controller setup can populate it, and so this
// hook's session-end handler can reset grab state (selectend doesn't fire when
// the session terminates).
export function useXRSession({ rendererRef, canvasRef, modelAnchorRef, overlayRef }) {
  const [xrActive, setXrActive] = useState(false)
  const sessionRef = useRef(null)
  const placementRef = useRef(null)
  const grabRef = useRef(null)
  const touchCleanupRef = useRef(null)

  const enterXR = useCallback(async (mode, { silent = false } = {}) => {
    const xr = typeof navigator !== 'undefined' ? navigator.xr : null
    // iOS Safari has no WebXR. Redirect to Variant Launch's hosted AR browser,
    // which reloads THIS url with navigator.xr injected — so on return the real
    // requestSession path below runs and AR actually starts. Only do this when
    // WebXR is genuinely absent: inside the Variant browser `xr` is defined, and
    // redirecting again there just re-shows the page without ever starting AR.
    if (mode === 'ar' && !xr && isIOSPhone()) {
      redirectToVariantLaunchAR()
      return
    }
    const renderer = rendererRef.current
    const container = canvasRef.current
    if (!renderer || !container) return
    if (!xr) {
      console.warn('WebXR not available on this browser')
      return
    }
    // iPhone-only advanced AR (runs inside the Variant Launch browser): reticle
    // tap-to-place, grow, passthrough, touch manipulation, transform panel.
    // Everything else (Quest / desktop headsets) uses the simple WebXR path
    // (just attach the session; model shows as-is) — the 2963c5f behaviour.
    const iosAR = mode === 'ar' && isIOSPhone()
    // Prefer the dedicated #overlay element as the dom-overlay root on iOS
    // (matches the Variant example); otherwise the canvas container.
    const overlayRoot = (overlayRef && overlayRef.current) || container
    try {
      const xrMode = mode === 'ar' ? 'immersive-ar' : 'immersive-vr'
      // iOS (Variant browser) wants hit-test + dom-overlay for the reticle/overlay
      // flow. Quest / desktop get a minimal init. Everything beyond the reference
      // space is OPTIONAL so the request can't be rejected for an unknown feature.
      let sessionInit
      if (iosAR) {
        sessionInit = {
          requiredFeatures: ['local'],
          optionalFeatures: ['local-floor', 'hit-test', 'dom-overlay'],
          domOverlay: { root: overlayRoot },
        }
      } else if (mode === 'ar') {
        sessionInit = { optionalFeatures: ['local-floor', 'dom-overlay'], domOverlay: { root: container } }
      } else {
        sessionInit = { optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking'] }
      }
      // Some runtimes reject otherwise-valid feature combos (e.g. Quest 3S has
      // returned "invalid requiredFeatures"). Fall back to a bare request so the
      // session still starts — the model just shows as-is.
      let session
      try {
        session = await xr.requestSession(xrMode, sessionInit)
      } catch (featErr) {
        console.warn('XR requestSession with init failed; retrying bare:', featErr)
        session = await xr.requestSession(xrMode)
      }
      const handleEnd = () => {
        session.removeEventListener('end', handleEnd)
        sessionRef.current = null
        setXrActive(false)
        // Restore the model's pre-XR pose on every device — the controller grab
        // (Quest) and AR placement (iOS) shouldn't leak into the desktop view.
        const anchor = modelAnchorRef.current
        const saved = placementRef.current
        if (anchor && saved) {
          anchor.position.copy(saved.position)
          anchor.quaternion.copy(saved.quaternion)
          anchor.scale.copy(saved.scale)
        }
        placementRef.current = null
        if (grabRef.current) grabRef.current.state = 'none'
        if (!iosAR) return
        // iOS AR teardown: restore the opaque clear, remove touch listeners,
        // reset the reticle/grow state.
        renderer.setClearColor(0x000000, 1)
        if (touchCleanupRef.current) {
          touchCleanupRef.current()
          touchCleanupRef.current = null
        }
        if (grabRef.current && grabRef.current.onSessionEnd) grabRef.current.onSessionEnd()
      }
      session.addEventListener('end', handleEnd)
      renderer.xr.setReferenceSpaceType(mode === 'ar' ? 'local' : 'local-floor')
      await renderer.xr.setSession(session)

      // Save the pre-XR pose for restore on exit (all devices).
      const anchor = modelAnchorRef.current
      if (anchor) {
        placementRef.current = {
          position: anchor.position.clone(),
          quaternion: anchor.quaternion.clone(),
          scale: anchor.scale.clone(),
        }
      }

      if (iosAR) {
        // Transparent clear so the camera passthrough shows behind the splats.
        renderer.setClearColor(0x000000, 0)
        // Quick Look-style touch gestures (active once the model is placed).
        touchCleanupRef.current = setupXRTouchControls({
          container: overlayRoot,
          rendererRef,
          modelAnchorRef,
          grabRef,
        })
        // Was hit-test granted? (undefined enabledFeatures → assume yes.)
        const hitTest = !session.enabledFeatures || session.enabledFeatures.includes('hit-test')
        if (grabRef.current && grabRef.current.onSessionStart) {
          grabRef.current.onSessionStart('ar', { hitTest })
        }
        if (!hitTest && grabRef.current) {
          grabRef.current.recenterDelay = 2
          grabRef.current.recenter = true
        }
      }
      // Non-iOS (Quest / desktop): nothing else — the model shows as-is.

      sessionRef.current = session
      setXrActive(true)
    } catch (err) {
      console.warn('XR session failed:', err)
      // On auto-start (return from Variant Launch) a failed gesture is expected
      // on some devices — stay quiet and let the user tap the AR button.
      if (!silent) alert(`Failed to enter ${mode.toUpperCase()}: ${err?.message || err}`)
    }
    // refs are stable; intentionally no deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exitXR = useCallback(() => {
    const session = sessionRef.current
    if (session) session.end().catch(() => {})
  }, [])

  // Returning from the Variant Launch AR browser: the url carries
  // `instantWebxr=true` and navigator.xr is now injected. Auto-start the AR
  // session so the user lands straight in AR instead of on a plain webpage
  // (which is what made it look like AR "didn't launch"). We wait for three.js
  // to create the renderer/canvas before requesting the session.
  useEffect(() => {
    if (!hasAutoStartMarker()) return
    const xr = navigator?.xr
    if (!xr) return

    let cancelled = false
    let tries = 0
    const tryStart = async () => {
      if (cancelled) return
      const supported = await xr.isSessionSupported('immersive-ar').catch(() => false)
      if (cancelled || !supported) return
      // Hold until the scene's renderer exists; enterXR no-ops without it.
      if (!rendererRef.current || !canvasRef.current) {
        if (tries++ < 100) setTimeout(tryStart, 100)
        return
      }
      clearAutoStartMarker() // so a manual refresh doesn't re-trigger
      enterXR('ar', { silent: true })
    }
    tryStart()
    return () => { cancelled = true }
    // refs/enterXR are stable; run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { xrActive, sessionRef, enterXR, exitXR, grabRef }
}

// Called inside the scene-init effect after renderer / scene / modelAnchor
// exist. Wires up:
//   - renderer.xr.enabled (so setAnimationLoop drives via the XR frame source
//     once a session attaches)
//   - two XR controllers added to scene
//   - selectstart / selectend listeners that drive a 1-hand drag / 2-hand
//     pinch-scale-rotate state machine
// Returns `{ updateXrGrab }` — call once per animate tick (no-op if !enableXR
// or no grab in progress).
export function setupXRControllers({ renderer, scene, modelAnchor, measureTarget, enableXR, grabRef, onArStatus, arOffset, arRotation }) {
  if (!enableXR) return { updateXrGrab: () => {} }

  // Enable the renderer XR hook so setAnimationLoop drives the XR frame source
  // once a session attaches — needed on ALL devices (Quest VR/AR included).
  renderer.xr.enabled = true

  // NOTE: the 6-DoF controller grab below runs on EVERY headset (Quest etc.).
  // The reticle / hit-test / grow / tap-to-place flow is iPhone-only — it stays
  // dormant on other devices because its flags (hitTestMode / recenter) are only
  // set by enterXR's iOS path. So Quest keeps the simple "show model + grab to
  // move/scale" behaviour without the AR placement UI.

  // SuperSplat grounding transform (position + rotation) that makes the model
  // rest upright on the origin; we re-apply it on placement so the model lands
  // on the reticle instead of floating. See MODEL_AR_OFFSET / MODEL_AR_ROTATION
  // in DualgsViewer.jsx (future: per-model manifest.json).
  // Composition: final = T(reticle) · S(scale) · T(offset) · R(rotation), which
  // collapses to anchor { position: reticle + offset·scale, quaternion: R, scale }.
  const offset = new THREE.Vector3(
    Array.isArray(arOffset) ? (arOffset[0] || 0) : 0,
    Array.isArray(arOffset) ? (arOffset[1] || 0) : 0,
    Array.isArray(arOffset) ? (arOffset[2] || 0) : 0,
  )
  const DEG = Math.PI / 180
  const correctionQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(
    (Array.isArray(arRotation) ? (arRotation[0] || 0) : 0) * DEG,
    (Array.isArray(arRotation) ? (arRotation[1] || 0) : 0) * DEG,
    (Array.isArray(arRotation) ? (arRotation[2] || 0) : 0) * DEG,
    'XYZ',
  ))

  const controller0 = renderer.xr.getController(0)
  const controller1 = renderer.xr.getController(1)
  scene.add(controller0)
  scene.add(controller1)

  // Hit-test reticle — the ring from the Variant example. Hidden until a surface
  // is found; tapping it places + grows the splat model there.
  const reticle = new THREE.Mesh(
    new THREE.RingGeometry(0.07, 0.1, 32).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9 }),
  )
  reticle.matrixAutoUpdate = false
  reticle.visible = false
  scene.add(reticle)

  // Single object held in the shared ref so the hook's session-end handler can
  // reset state without re-establishing listeners. `recenter` drives VR placement;
  // `placed` / `hitTestMode` drive the AR tap-to-place flow.
  const grab = {
    state: 'none', // 'none' | 'left' | 'right' | 'two'  (6-DoF controllers)
    modelPos0: new THREE.Vector3(),
    modelQuat0: new THREE.Quaternion(),
    modelScale0: new THREE.Vector3(),
    leftPos0: new THREE.Vector3(),
    rightPos0: new THREE.Vector3(),
    dist0: 0,
    mid0: new THREE.Vector3(),
    yaw0: 0,
    recenter: false,
    recenterDelay: 0,   // frames to wait so the XR camera pose is live before placing
    placed: false,      // AR: model has been dropped on a surface
    hitTestMode: false, // AR: tap-to-place flow is active
  }
  grabRef.current = grab

  // Reusable scratch buffers — avoid per-frame allocation in the rAF hot path.
  const leftPos = new THREE.Vector3()
  const rightPos = new THREE.Vector3()
  const mid = new THREE.Vector3()
  const delta = new THREE.Vector3()

  // Scratch for placement / sizing.
  const camPos = new THREE.Vector3()
  const camQuat = new THREE.Quaternion()
  const camScale = new THREE.Vector3()
  const camFwd = new THREE.Vector3()
  const box = new THREE.Box3()
  const sizeVec = new THREE.Vector3()
  const target = new THREE.Vector3()
  const reticlePos = new THREE.Vector3()
  const reticleQuat = new THREE.Quaternion()
  const reticleScale = new THREE.Vector3()

  const PLACE_DISTANCE = 1.3 // metres in front of the viewer (VR)
  const MAX_AR_SIZE = 2.0    // shrink only if the model is larger than this (metres)
  const PLACE_DROP = 0.15    // VR: sit it slightly below eye level
  const GROW_MS = 500        // tap-to-grow animation duration

  // Hit-test + grow state.
  let hitTestSource = null
  let hitTestSourceRequested = false
  let lastStatus = null
  let growActive = false
  let growStart = 0
  const placeData = { px: 0, py: 0, pz: 0, s: 1 }

  const now = () => (typeof performance !== 'undefined' ? performance.now() : 0)
  const setStatus = (s) => {
    if (s === lastStatus) return
    lastStatus = s
    if (onArStatus) onArStatus(s)
  }

  // Adaptive scale: captures are metric, so keep natural size and only shrink if
  // larger than MAX_AR_SIZE so it fits a room. (Bounding box of splat meshes can
  // be unreliable; the model CENTRE is handled by the manual `offset` instead.)
  //
  // Measure the PERFORMER only, never the whole anchor: a persistent scene 3DGS
  // is a room-sized sibling, so boxing the anchor would blow maxDim up and
  // shrink the performer to a speck. `measureTarget` is the sequence layer;
  // it falls back to the anchor when there is no static layer.
  const measureScale = () => {
    modelAnchor.scale.setScalar(1)
    modelAnchor.updateMatrixWorld(true)
    box.makeEmpty()
    box.setFromObject(measureTarget || modelAnchor)
    let s = 1
    sizeVec.set(0, 0, 0)
    if (!box.isEmpty()) {
      box.getSize(sizeVec)
      const maxDim = Math.max(sizeVec.x, sizeVec.y, sizeVec.z)
      if (isFinite(maxDim) && maxDim > MAX_AR_SIZE) s = MAX_AR_SIZE / maxDim
    }
    return s
  }

  // VR: drop the model in front of the live camera, natural-ish scale.
  const placeInFront = () => {
    const cam = renderer.xr.getCamera ? renderer.xr.getCamera() : null
    if (!cam) return
    cam.matrixWorld.decompose(camPos, camQuat, camScale)
    camFwd.set(0, 0, -1).applyQuaternion(camQuat).normalize()
    const s = measureScale()
    const dist = PLACE_DISTANCE + Math.max(sizeVec.x, sizeVec.y, sizeVec.z) * s * 0.5
    target.copy(camPos).addScaledVector(camFwd, dist)
    target.y -= PLACE_DROP
    modelAnchor.scale.setScalar(s)
    modelAnchor.quaternion.copy(correctionQuat)
    // Apply the SuperSplat grounding transform so the model rests on `target`.
    modelAnchor.position.copy(target).addScaledVector(offset, s)
    modelAnchor.visible = true
  }

  // AR: drop + grow the model at the reticle (on the detected surface).
  const placeAtReticle = () => {
    const s = measureScale()
    reticle.matrix.decompose(reticlePos, reticleQuat, reticleScale)
    placeData.px = reticlePos.x
    placeData.py = reticlePos.y
    placeData.pz = reticlePos.z
    placeData.s = s
    modelAnchor.quaternion.copy(correctionQuat)
    modelAnchor.visible = true
    growActive = true
    growStart = now()
    grab.placed = true
    reticle.visible = false
    setStatus('placed')
  }

  // Apply the current grow scale, keeping the base on the surface + centred on
  // the reticle. Called each frame while growing.
  const applyGrow = () => {
    let t = (now() - growStart) / GROW_MS
    if (t < 0) t = 0
    if (t > 1) t = 1
    const e = 1 - Math.pow(1 - t, 3) // easeOutCubic
    const cur = placeData.s * (0.04 + 0.96 * e)
    modelAnchor.scale.setScalar(cur)
    modelAnchor.quaternion.copy(correctionQuat)
    // Apply the SuperSplat grounding transform (offset scaled by the live grow
    // scale) so the model grows in resting on the reticle.
    modelAnchor.position.set(
      placeData.px + offset.x * cur,
      placeData.py + offset.y * cur,
      placeData.pz + offset.z * cur,
    )
    if (t >= 1) growActive = false
  }

  // Public hooks used by the session lifecycle + tap handlers.
  grab.tryPlace = () => {
    if (grab.hitTestMode && !grab.placed && reticle.visible) placeAtReticle()
  }
  // Re-enter the tap-to-place flow (hide model, show reticle again).
  grab.replace = () => {
    if (!grab.hitTestMode) return
    grab.placed = false
    growActive = false
    reticle.visible = false
    modelAnchor.visible = false
    setStatus('searching')
  }
  grab.onSessionStart = (mode, opts = {}) => {
    growActive = false
    hitTestSource = null
    hitTestSourceRequested = false
    reticle.visible = false
    lastStatus = null
    if (mode === 'ar' && opts.hitTest) {
      // Handheld / hit-test capable: tap-to-place flow (model hidden until tap).
      grab.hitTestMode = true
      grab.placed = false
      modelAnchor.visible = false
      setStatus('searching')
    } else {
      // VR, or AR on a headset without hit-test: place in front immediately
      // (caller sets recenter). Mark placed so touch manipulation works too.
      grab.hitTestMode = false
      grab.placed = true
      modelAnchor.visible = true
      setStatus(mode === 'ar' ? 'placed' : null)
    }
  }
  grab.onSessionEnd = () => {
    grab.hitTestMode = false
    grab.placed = false
    grab.state = 'none'
    growActive = false
    hitTestSource = null
    hitTestSourceRequested = false
    reticle.visible = false
    modelAnchor.visible = true
    setStatus(null)
  }

  // Treat controller 0 as "left", controller 1 as "right" — three.js doesn't
  // guarantee handedness but the math is symmetric.
  const snapshot = () => {
    grab.modelPos0.copy(modelAnchor.position)
    grab.modelQuat0.copy(modelAnchor.quaternion)
    grab.modelScale0.copy(modelAnchor.scale)
    controller0.getWorldPosition(grab.leftPos0)
    controller1.getWorldPosition(grab.rightPos0)
    if (grab.state === 'two') {
      grab.dist0 = grab.leftPos0.distanceTo(grab.rightPos0)
      grab.mid0.addVectors(grab.leftPos0, grab.rightPos0).multiplyScalar(0.5)
      grab.yaw0 = Math.atan2(
        grab.rightPos0.x - grab.leftPos0.x,
        grab.rightPos0.z - grab.leftPos0.z,
      )
    }
  }
  // Screen (handheld) selects place/grow + DOM-touch manipulate, NOT controller
  // grab — so ignore them here; only tracked-pointer (headset) drives the grab.
  const onSelectStart = (which) => (event) => {
    if (event && event.data && event.data.targetRayMode === 'screen') return
    if (grab.state === 'none') grab.state = which === 0 ? 'left' : 'right'
    else grab.state = 'two'
    snapshot()
  }
  const onSelectEnd = (which) => (event) => {
    if (event && event.data && event.data.targetRayMode === 'screen') return
    const stopped = which === 0 ? 'left' : 'right'
    if (grab.state === 'two') {
      grab.state = stopped === 'left' ? 'right' : 'left'
      snapshot()
    } else if (grab.state === stopped) {
      grab.state = 'none'
    }
  }
  const onSelect = () => grab.tryPlace()
  controller0.addEventListener('selectstart', onSelectStart(0))
  controller0.addEventListener('selectend', onSelectEnd(0))
  controller0.addEventListener('select', onSelect)
  controller1.addEventListener('selectstart', onSelectStart(1))
  controller1.addEventListener('selectend', onSelectEnd(1))

  const updateXrGrab = (frame) => {
    if (growActive) applyGrow()

    // VR: one-shot place-in-front (wait a couple of frames for a live pose).
    if (grab.recenter) {
      if (grab.recenterDelay > 0) grab.recenterDelay--
      else { placeInFront(); grab.recenter = false }
    }

    // AR: run hit-test + move the reticle until the model is placed.
    if (grab.hitTestMode && !grab.placed && frame) {
      const session = renderer.xr.getSession ? renderer.xr.getSession() : null
      const referenceSpace = renderer.xr.getReferenceSpace ? renderer.xr.getReferenceSpace() : null
      if (session && referenceSpace) {
        if (!hitTestSourceRequested && session.requestHitTestSource) {
          hitTestSourceRequested = true
          session.requestReferenceSpace('viewer')
            .then((vs) => session.requestHitTestSource({ space: vs }))
            .then((src) => { hitTestSource = src })
            .catch(() => {})
        }
        if (hitTestSource) {
          const results = frame.getHitTestResults(hitTestSource)
          if (results.length) {
            const pose = results[0].getPose(referenceSpace)
            if (pose) {
              reticle.visible = true
              reticle.matrix.fromArray(pose.transform.matrix)
              setStatus('ready')
            }
          } else {
            reticle.visible = false
            setStatus('searching')
          }
        }
      }
    }

    // 6-DoF controllers (headset). Handheld AR is event-driven (touch + tap).
    if (grab.state === 'none') return
    controller0.getWorldPosition(leftPos)
    controller1.getWorldPosition(rightPos)

    if (grab.state === 'two') {
      // Scale = distance ratio, rotation = yaw delta, translation = midpoint shift.
      const dist = leftPos.distanceTo(rightPos)
      mid.addVectors(leftPos, rightPos).multiplyScalar(0.5)
      const scale = dist / (grab.dist0 || 1)
      const yaw = Math.atan2(rightPos.x - leftPos.x, rightPos.z - leftPos.z)
      modelAnchor.scale.copy(grab.modelScale0).multiplyScalar(scale)
      modelAnchor.quaternion.copy(grab.modelQuat0)
      modelAnchor.rotateY(yaw - grab.yaw0)
      delta.subVectors(mid, grab.mid0)
      modelAnchor.position.copy(grab.modelPos0).add(delta)
    } else {
      // One-hand drag — translation only.
      const live = grab.state === 'left' ? leftPos : rightPos
      const initial = grab.state === 'left' ? grab.leftPos0 : grab.rightPos0
      delta.subVectors(live, initial)
      modelAnchor.position.copy(grab.modelPos0).add(delta)
    }
  }

  return { updateXrGrab }
}

// Handheld-AR touch manipulation, modelled on iOS USDZ AR Quick Look:
//   • 1 finger drag   → translate the model across the ground plane (no rotate)
//   • 2 finger pinch  → uniform scale
//   • 2 finger twist  → rotate (yaw) around the vertical axis
// Driven by DOM TouchEvents on the dom-overlay root. The WebXR 'screen' input
// only tracks the PRIMARY pointer (so it can't do pinch); event.touches gives
// every active finger, which is why two-finger gestures need this path. Returns
// a cleanup that removes the listeners. No-op without a container.
export function setupXRTouchControls({ container, rendererRef, modelAnchorRef, grabRef }) {
  if (!container) return () => {}

  const MOVE_K = 0.0016 // base world units per pixel, scaled by view distance
  const MIN_SCALE = 0.01
  const MAX_SCALE = 50
  const TAP_MOVE_PX = 10  // movement under this still counts as a tap
  const TAP_MAX_MS = 400  // duration under this still counts as a tap
  const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : 0)
  const isPlaced = () => !!(grabRef && grabRef.current && grabRef.current.placed)

  const camPos = new THREE.Vector3()
  const camQuat = new THREE.Quaternion()
  const camScale = new THREE.Vector3()
  const right = new THREE.Vector3()
  const fwd = new THREE.Vector3()
  const yAxis = new THREE.Vector3(0, 1, 0)
  const tmpQ = new THREE.Quaternion()

  // Ground-plane camera basis: right + forward flattened onto the horizontal
  // plane, so one-finger drag slides the model along the floor like Quick Look.
  const readCam = () => {
    const r = rendererRef.current
    const cam = r && r.xr && r.xr.getCamera ? r.xr.getCamera() : null
    if (!cam) return false
    cam.matrixWorld.decompose(camPos, camQuat, camScale)
    right.setFromMatrixColumn(cam.matrixWorld, 0).setY(0)
    if (right.lengthSq() < 1e-6) right.set(1, 0, 0)
    right.normalize()
    fwd.set(0, 0, -1).applyQuaternion(camQuat).setY(0)
    if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1)
    fwd.normalize()
    return true
  }

  let prev = null // { pts:[{x,y}...], dist, angle }
  let tap = null  // { t, x, y, moved } — single-finger tap for place-on-tap fallback
  const snap = (touches) => {
    const pts = []
    for (let i = 0; i < touches.length; i++) {
      pts.push({ x: touches[i].clientX, y: touches[i].clientY })
    }
    const s = { pts, dist: 0, angle: 0 }
    if (pts.length >= 2) {
      s.dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
      s.angle = Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x)
    }
    return s
  }

  // Touches that start on an overlay UI element (the transform panel) must not
  // manipulate the model — let them through to the input/button untouched.
  const onUI = (e) => !!(e.target && e.target.closest && e.target.closest('[data-ar-ui]'))

  const onStart = (e) => {
    if (onUI(e)) { prev = null; tap = null; return }
    if (e.cancelable) e.preventDefault()
    tap = e.touches.length === 1
      ? { t: nowMs(), x: e.touches[0].clientX, y: e.touches[0].clientY, moved: false }
      : null
    prev = snap(e.touches)
  }

  const onMove = (e) => {
    if (onUI(e)) return
    if (e.cancelable) e.preventDefault()
    const cur = snap(e.touches)
    // Track tap invalidation (movement / extra fingers).
    if (tap && cur.pts.length === 1) {
      if (Math.hypot(cur.pts[0].x - tap.x, cur.pts[0].y - tap.y) > TAP_MOVE_PX) tap.moved = true
    } else {
      tap = null
    }
    // Manipulation only applies once the model is placed (AR tap-to-place flow).
    if (!isPlaced()) { prev = cur; return }
    const anchor = modelAnchorRef.current
    if (!anchor) { prev = cur; return }
    // Re-seed (no transform) when the finger count changes, so adding/removing a
    // finger doesn't cause a jump.
    if (!prev || prev.pts.length !== cur.pts.length) { prev = cur; return }

    if (cur.pts.length === 1) {
      // Translate on the ground plane.
      const dx = cur.pts[0].x - prev.pts[0].x
      const dy = cur.pts[0].y - prev.pts[0].y
      if (readCam()) {
        const depth = camPos.distanceTo(anchor.position) || 1
        const k = MOVE_K * depth
        anchor.position.addScaledVector(right, dx * k)   // screen X → ground right
        anchor.position.addScaledVector(fwd, -dy * k)    // drag up → push away
      }
    } else if (cur.pts.length >= 2) {
      // Pinch → scale.
      if (prev.dist > 0 && cur.dist > 0) {
        let s = anchor.scale.x * (cur.dist / prev.dist)
        s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s))
        anchor.scale.setScalar(s)
      }
      // Twist → yaw rotate.
      let dA = cur.angle - prev.angle
      if (dA > Math.PI) dA -= 2 * Math.PI
      else if (dA < -Math.PI) dA += 2 * Math.PI
      tmpQ.setFromAxisAngle(yAxis, -dA)
      anchor.quaternion.premultiply(tmpQ)
    }
    prev = cur
  }

  const onEnd = (e) => {
    // Tap-to-place fallback: a quick single-finger tap before the model is placed
    // grows it at the reticle (in case the WebXR controller 'select' doesn't fire).
    if (!isPlaced() && tap && !tap.moved && e.touches.length === 0) {
      if (nowMs() - tap.t < TAP_MAX_MS && grabRef && grabRef.current && grabRef.current.tryPlace) {
        grabRef.current.tryPlace()
      }
    }
    if (!e.touches.length) tap = null
    prev = e.touches.length ? snap(e.touches) : null
  }

  container.addEventListener('touchstart', onStart, { passive: false })
  container.addEventListener('touchmove', onMove, { passive: false })
  container.addEventListener('touchend', onEnd)
  container.addEventListener('touchcancel', onEnd)

  return () => {
    container.removeEventListener('touchstart', onStart)
    container.removeEventListener('touchmove', onMove)
    container.removeEventListener('touchend', onEnd)
    container.removeEventListener('touchcancel', onEnd)
    prev = null
  }
}

// Toolbar XR enter/exit button — prefers AR when both are supported. Hidden
// unless `enableXR` is set AND the browser reports support for at least one
// mode (via `useXRSupport`).
export function XRButton({ enableXR, xrSupport, xrActive, onEnter, onExit, ctrlBtn }) {
  if (!enableXR) return null
  if (!xrSupport.vr && !xrSupport.ar) return null
  return (
    <button
      onClick={() => {
        if (xrActive) onExit()
        else onEnter(xrSupport.ar ? 'ar' : 'vr')
      }}
      title={xrActive ? 'Exit XR' : (xrSupport.ar ? 'Enter AR' : 'Enter VR')}
      style={{
        ...ctrlBtn,
        padding: '4px 12px',
        background: xrActive ? 'rgba(80,200,120,0.28)' : ctrlBtn.background,
        border: `1px solid ${xrActive ? 'rgba(80,200,120,0.6)' : 'rgba(255,255,255,0.18)'}`,
      }}
    >
      {xrActive ? 'Exit XR' : (xrSupport.ar ? 'AR' : 'VR')}
    </button>
  )
}

// ── Model transform panel ──────────────────────────────────────────────────
const tpInput = {
  width: 72,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: 14,
  textAlign: 'center',
}
const tpStep = {
  width: 30,
  height: 30,
  flex: '0 0 auto',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.08)',
  color: '#fff',
  fontSize: 18,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  touchAction: 'none',
}
const tpPanel = {
  position: 'absolute',
  top: 14,
  left: 14,
  zIndex: 30,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  borderRadius: 12,
  background: 'rgba(18,18,18,0.86)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.14)',
  pointerEvents: 'auto',
  userSelect: 'none',
}

// On-screen control for precise model placement — position X/Y/Z + uniform
// scale. Works in the normal viewer and (via a portal into the dom-overlay
// root) inside handheld AR, where pinch/drag alone is imprecise. It reads the
// live `modelAnchor` each frame so the fields track touch-gesture changes, and
// writes straight back on input. The `[data-ar-ui]` marker tells the AR touch
// handler to ignore touches that land here (so editing doesn't move the model).
export function ModelTransformPanel({ modelAnchorRef, inAR = false, onClose }) {
  const [vals, setVals] = useState({ x: '0', y: '0', z: '0', scale: '1' })
  const editingRef = useRef(false)

  // Mirror the live anchor into the fields (unless the user is mid-edit).
  useEffect(() => {
    let raf = 0
    const round = (n) => Math.round(n * 1000) / 1000
    const tick = () => {
      const a = modelAnchorRef.current
      if (a && !editingRef.current) {
        const x = round(a.position.x)
        const y = round(a.position.y)
        const z = round(a.position.z)
        const s = round(a.scale.x)
        setVals((p) =>
          Number(p.x) === x && Number(p.y) === y && Number(p.z) === z && Number(p.scale) === s
            ? p
            : { x: String(x), y: String(y), z: String(z), scale: String(s) },
        )
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [modelAnchorRef])

  const applyField = (key, raw) => {
    setVals((p) => ({ ...p, [key]: raw }))
    const a = modelAnchorRef.current
    if (!a) return
    const v = Number(raw)
    if (!Number.isFinite(v)) return
    if (key === 'scale') { if (v > 0) a.scale.setScalar(v) }
    else a.position[key] = v
  }

  const nudge = (key, delta) => {
    const a = modelAnchorRef.current
    if (!a) return
    if (key === 'scale') a.scale.setScalar(Math.max(0.001, a.scale.x + delta))
    else a.position[key] += delta
  }

  const row = (key, label, step) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} key={key}>
      <span style={{ width: 16, color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{label}</span>
      <button
        style={tpStep}
        onPointerDown={(e) => { e.preventDefault(); nudge(key, -step) }}
      >−</button>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={vals[key]}
        onFocus={() => { editingRef.current = true }}
        onBlur={() => { editingRef.current = false }}
        onChange={(e) => applyField(key, e.target.value)}
        style={tpInput}
      />
      <button
        style={tpStep}
        onPointerDown={(e) => { e.preventDefault(); nudge(key, step) }}
      >+</button>
    </div>
  )

  return (
    <div data-ar-ui style={tpPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
          {inAR ? 'AR Transform' : 'Transform'}
        </span>
        {onClose && (
          <button
            style={{ ...tpStep, width: 26, height: 26, fontSize: 16 }}
            onPointerDown={(e) => { e.preventDefault(); onClose() }}
          >×</button>
        )}
      </div>
      {row('x', 'X', 0.05)}
      {row('y', 'Y', 0.05)}
      {row('z', 'Z', 0.05)}
      {row('scale', 'S', 0.05)}
    </div>
  )
}
